import React from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

interface EditableListProps {
  label: string;
  items: string[] | undefined;
  setItems: React.Dispatch<React.SetStateAction<string[]>> | undefined;
  mode: 'view' | 'edit' | 'add';
}

export const EditableListComponent: React.FC<EditableListProps> = ({
  label,
  items,
  setItems,
  mode,
}) => {
  const handleAddItem = () => {
    if (setItems) {
      setItems((prevItems) => [...(prevItems || []), '']);
    }
  };

  const handleDeleteItem = (index: number) => {
    if (setItems) {
      setItems((prevItems) => prevItems.filter((_, i) => i !== index));
    }
  };

  const handleChangeItem = (index: number, value: string) => {
    if (setItems) {
      setItems((prevItems) => {
        const newItems = [...prevItems];
        newItems[index] = value;
        return newItems;
      });
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 2,
        maxHeight: '100%',
        height: '100%',
        minHeight: '100%',
        overflowY: 'auto',
        scrollbarWidth: 'none',
        width: '100%',
      }}
    >
      <Typography
        variant="h6"
        sx={{ marginBottom: 2, fontWeight: 'bold', fontSize: '1.25rem' }}
      >
        {label}
      </Typography>

      {mode === 'view' ? (
        <Box>
          {Array.isArray(items) && items.length > 0 ? (
            <>
              <List sx={{ paddingLeft: 0 }}>
                {items.map((item, index) => (
                  <ListItem key={index} sx={{ paddingLeft: 0 }}>
                    <ListItemText
                      primary={`• ${item}`}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: {
                          fontSize: '1rem',
                          lineHeight: '1.7',
                          color: 'text.secondary',
                          marginBottom: 1,
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ margin: '16px 0' }} />
            </>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontStyle: 'italic' }}
            >
              No items available.
            </Typography>
          )}
        </Box>
      ) : (
        Array.isArray(items) &&
        items.map((item, index) => (
          <TextField
            key={index}
            label={`${label} ${index + 1}`}
            value={item}
            onChange={(e) => handleChangeItem(index, e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleDeleteItem(index)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ marginBottom: 2 }}
          />
        ))
      )}

      {mode !== 'view' && (
        <Button
          onClick={handleAddItem}
          startIcon={<Add />}
          sx={{ marginTop: 2 }}
        >
          Add Another {label}
        </Button>
      )}
    </Paper>
  );
};
